import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, Loader2, Save, Timer } from 'lucide-react';
import { swatidService } from '@/services';
import type { ApiSwatidRelay } from '@/types/api';
import { SWATID_RELAY_CONFIG_MODES } from '@/constants/swatid';
import { formatTimerRemaining } from '@/utils/swatidValidation';
import {
  buildRelayConfigPayload,
  getSwatidRelayDisplayName,
  isCustomSwatidRelayName,
  validateSwatidRelayName,
} from '@/utils/swatidRelay';
import { useSwatidRefresh } from '@/hooks/useSwatidController';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { SwatidRelayToggleButton } from './SwatidRelayToggleButton';

interface SwatidRelayDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controllerId: string;
  relay: ApiSwatidRelay | null;
  canControl: boolean;
  onConfigUpdated?: () => void;
}

export function SwatidRelayDetailDialog({
  open,
  onOpenChange,
  controllerId,
  relay,
  canControl,
  onConfigUpdated,
}: SwatidRelayDetailDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { refreshAll } = useSwatidRefresh(controllerId);
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'fijo' | 'temporizado'>('temporizado');
  const [defaultDuration, setDefaultDuration] = useState(300);
  const [customDuration, setCustomDuration] = useState(300);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const resetForm = (r: ApiSwatidRelay) => {
    setName(r.name ?? '');
    setMode(r.mode);
    setDefaultDuration(r.defaultDuration > 0 ? r.defaultDuration : 300);
    setCustomDuration(r.defaultDuration > 0 ? r.defaultDuration : 300);
    setNameError(null);
  };

  useEffect(() => {
    if (open && relay) resetForm(relay);
  }, [open, relay?.relayNumber]);

  const updateRelayCache = (updated: ApiSwatidRelay) => {
    queryClient.setQueryData<ApiSwatidRelay[]>(['swatid-relays', controllerId], (old) => {
      if (!old?.length) return old;
      return old.map((r) => (r.relayNumber === updated.relayNumber ? { ...r, ...updated } : r));
    });
  };

  const nameMutation = useMutation({
    mutationFn: async () => {
      if (!relay) throw new Error('Sin relé');
      const validationError = validateSwatidRelayName(name);
      if (validationError) throw new Error(validationError);

      const res = await swatidService.updateRelayName(
        controllerId,
        relay.relayNumber,
        name.trim()
      );
      if (!res.success) throw new Error(res.error?.message || 'Error al guardar nombre');
      if (!res.data) throw new Error('El servidor no devolvió el relé actualizado');
      return res.data;
    },
    onSuccess: async (updated) => {
      updateRelayCache(updated);
      setName(updated.name);
      await refreshAll(500);
      onConfigUpdated?.();
      toast({
        title: 'Nombre guardado',
        description: `"${getSwatidRelayDisplayName(updated)}"`,
      });
    },
    onError: (e: Error) => {
      setNameError(e.message);
      toast({ title: 'No se pudo guardar el nombre', description: e.message, variant: 'destructive' });
    },
  });

  const relayConfigMutation = useMutation({
    mutationFn: async () => {
      if (!relay) throw new Error('Sin relé');
      const payload = buildRelayConfigPayload({ mode, defaultDuration });
      const res = await swatidService.updateRelayConfig(
        controllerId,
        relay.relayNumber,
        payload
      );
      if (!res.success) throw new Error(res.error?.message || 'Error al guardar configuración');
      if (!res.data) throw new Error('El servidor no devolvió la configuración actualizada');
      return res.data;
    },
    onSuccess: async (updated) => {
      updateRelayCache(updated);
      await refreshAll(500);
      onConfigUpdated?.();
      toast({ title: 'Configuración de relé guardada' });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  const timedOnMutation = useMutation({
    mutationFn: async (duration: number) => {
      if (!relay) throw new Error('Sin relé');
      const res = await swatidService.controlRelay(controllerId, relay.relayNumber, {
        action: 'on',
        duration,
      });
      if (!res.success) throw new Error(res.error?.message || 'Error al controlar relé');
      return res.data;
    },
    onSuccess: async () => {
      await refreshAll(1000);
      toast({ title: 'Relé encendido', description: 'Temporización activa.' });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!relay) throw new Error('Sin relé');
      const res = await swatidService.controlRelay(controllerId, relay.relayNumber, {
        action: 'cancel',
      });
      if (!res.success) throw new Error(res.error?.message || 'Error al cancelar');
      return res.data;
    },
    onSuccess: async () => {
      await refreshAll(1000);
      toast({ title: 'Temporizador cancelado' });
    },
    onError: (e: Error) => {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    },
  });

  if (!relay) return null;

  const handleOpen = (isOpen: boolean) => {
    if (isOpen && relay) resetForm(relay);
    onOpenChange(isOpen);
  };

  const busy =
    nameMutation.isPending ||
    relayConfigMutation.isPending ||
    timedOnMutation.isPending ||
    cancelMutation.isPending;

  const savedName = relay.name?.trim() ?? '';
  const nameChanged = name.trim() !== savedName;
  const hasCustomName = isCustomSwatidRelayName(relay);
  const configChanged =
    mode !== relay.mode ||
    defaultDuration !== (relay.defaultDuration > 0 ? relay.defaultDuration : 300);

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Relé {relay.relayNumber}
            {hasCustomName ? (
              <Badge variant="secondary" className="text-xs font-normal">
                {getSwatidRelayDisplayName(relay)}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                Sin nombre personalizado
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Personalice el nombre para identificarlo en jardines, programaciones y el panel de
            relés. El valor por defecto del backend es &quot;Relé N&quot;.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Estado:</span>
            <Badge className={cn(relay.currentState ? 'bg-success' : '')}>
              {relay.currentState ? 'Encendido' : 'Apagado'}
            </Badge>
            {relay.timerRemaining > 0 && (
              <Badge variant="outline" className="gap-1">
                <Timer className="w-3 h-3" />
                {formatTimerRemaining(relay.timerRemaining)}
              </Badge>
            )}
          </div>

          <div className="space-y-2 rounded-lg border p-3 bg-muted/20">
            <Label htmlFor="relay-name">Nombre del relé</Label>
            <Input
              id="relay-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError(null);
              }}
              placeholder={`Ej: Zona Norte (por defecto: Relé ${relay.relayNumber})`}
              maxLength={100}
              disabled={busy}
            />
            {nameError && <p className="text-xs text-destructive">{nameError}</p>}
            <Button
              className="w-full"
              disabled={busy || !name.trim() || !nameChanged}
              onClick={() => nameMutation.mutate()}
            >
              {nameMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar nombre
            </Button>
          </div>

          {canControl ? (
            <div className="space-y-2">
              <SwatidRelayToggleButton
                controllerId={controllerId}
                relay={relay}
                size="default"
                className="h-10"
              />
              {relay.timerRemaining > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  disabled={busy}
                  onClick={() => cancelMutation.mutate()}
                >
                  {cancelMutation.isPending && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  Cancelar temporizador
                </Button>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              El nombre se puede guardar en cualquier modo. El control on/off requiere modo Remota.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
            <div className="space-y-2">
              <Label>Modo relé</Label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as 'fijo' | 'temporizado')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                disabled={busy}
              >
                {SWATID_RELAY_CONFIG_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m === 'fijo' ? 'Fijo' : 'Temporizado'}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Duración def. (s)</Label>
              <Input
                type="number"
                min={1}
                value={defaultDuration}
                onChange={(e) => setDefaultDuration(Number(e.target.value) || 300)}
                disabled={busy || mode === 'fijo'}
              />
            </div>
          </div>

          <Button
            variant="secondary"
            className="w-full"
            disabled={busy || !configChanged}
            onClick={() => relayConfigMutation.mutate()}
          >
            {relayConfigMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Guardar modo y duración
          </Button>

          {canControl && (
            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full gap-1">
                  <ChevronDown
                    className={cn('w-4 h-4 transition-transform', advancedOpen && 'rotate-180')}
                  />
                  Encendido con duración personalizada
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || relay.currentState}
                    onClick={() => timedOnMutation.mutate(300)}
                  >
                    5 min
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || relay.currentState}
                    onClick={() => timedOnMutation.mutate(900)}
                  >
                    15 min
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={1}
                    value={customDuration}
                    onChange={(e) => setCustomDuration(Number(e.target.value) || 300)}
                    className="flex-1"
                    disabled={relay.currentState}
                  />
                  <Button
                    disabled={busy || relay.currentState}
                    onClick={() => timedOnMutation.mutate(customDuration)}
                  >
                    Encender
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
